import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ICON_OPTIONS, COLOR_OPTIONS, getIcon, getColor, slugify } from '@/lib/icons';
import { IconCropper } from '@/components/IconCropper';
import { Loader2, Check, Upload, Trash2, Crop, ExternalLink } from 'lucide-react';

const MAX_SOURCE = 15_000_000; // raw file guard before cropping/downscaling
const EMPTY = {
  name: '', slug: '', url: '', description: '', icon: 'AppWindow', icon_image: '',
  color: 'teal', category: '', enabled: true, open_in_new_tab: false,
};

export function AppFormDialog({ open, onOpenChange, initial, onSave, saving }) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [err, setErr] = useState('');
  const [cropSrc, setCropSrc] = useState('');
  const [cropperOpen, setCropperOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setSlugTouched(Boolean(initial));
      setErr('');
      setCropSrc('');
      setCropperOpen(false);
    }
  }, [open, initial]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onNameChange = (v) => {
    setForm((f) => ({ ...f, name: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const onIconFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Please choose an image file.'); return; }
    if (file.size > MAX_SOURCE) { setErr('That image is too large. Use one under ~15MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setErr('');
      setCropSrc(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropped = (dataUrl) => {
    setForm((f) => ({ ...f, icon_image: dataUrl }));
    setCropperOpen(false);
    setCropSrc('');
  };

  const recrop = () => {
    if (form.icon_image) {
      setCropSrc(form.icon_image);
      setCropperOpen(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    if (!form.slug.trim()) { setErr('Shortcut is required.'); return; }
    const payload = {
      ...form,
      name: form.name.trim(),
      slug: slugify(form.slug),
      url: form.url.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
    };
    try {
      await onSave(payload);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not save. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 bg-[color:var(--cx-surface-solid)] border-[color:var(--cx-border)]"
        onInteractOutside={(e) => { if (cropperOpen) e.preventDefault(); }}>
        <DialogHeader className="p-6 pb-3">
          <DialogTitle className="font-display">{isEdit ? 'Edit app' : 'Add a new app'}</DialogTitle>
          <DialogDescription>Configure how this app appears and opens in your portal.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit}>
          <ScrollArea className="max-h-[60vh] px-6">
            <div className="space-y-4 pb-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-[color:var(--cx-fg-muted)]">Name</Label>
                  <Input value={form.name} onChange={(e) => onNameChange(e.target.value)} placeholder="Whip Tracker" data-testid="app-form-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[color:var(--cx-fg-muted)]">Shortcut</Label>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-sm text-[color:var(--cx-muted)]">/</span>
                    <Input value={form.slug} onChange={(e) => { setSlugTouched(true); set('slug', e.target.value); }} placeholder="whip-tracker" className="font-mono" data-testid="app-form-slug" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[color:var(--cx-fg-muted)]">Category</Label>
                  <Input value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="Operations" data-testid="app-form-category" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[color:var(--cx-fg-muted)]">App URL</Label>
                <Input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://schedule.cxassistant.net" className="font-mono text-sm" data-testid="app-form-url" />
                <p className="text-xs text-[color:var(--cx-muted)]">Leave empty for now if you’ll add it later.</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[color:var(--cx-fg-muted)]">Description</Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Short description of what this app does." rows={2} data-testid="app-form-description" />
              </div>

              {/* App icon */}
              <div className="space-y-2">
                <Label className="text-[color:var(--cx-fg-muted)]">App icon</Label>
                <div className="flex items-center gap-3">
                  <span className="h-12 w-12 rounded-xl overflow-hidden border border-[color:var(--cx-border)] flex items-center justify-center shrink-0"
                    style={form.icon_image ? {} : { background: `linear-gradient(135deg, ${getColor(form.color).from}22, ${getColor(form.color).to}14)` }}>
                    {form.icon_image ? (
                      <img src={form.icon_image} alt="icon" className="h-full w-full object-cover" data-testid="app-form-icon-image-preview" />
                    ) : (
                      React.createElement(getIcon(form.icon), { className: 'h-5 w-5', style: { color: getColor(form.color).from } })
                    )}
                  </span>
                  <label className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--cx-border)] px-3 py-2 text-sm text-[color:var(--cx-fg-muted)] hover:border-[rgba(46,230,197,0.4)] hover:text-[color:var(--cx-fg)] cursor-pointer transition-colors">
                    <Upload className="h-4 w-4" />Upload &amp; crop
                    <input type="file" accept="image/*" className="hidden" onChange={onIconFile} data-testid="app-form-icon-image-input" />
                  </label>
                  {form.icon_image && (
                    <Button type="button" variant="ghost" size="sm" onClick={recrop} data-testid="app-form-icon-recrop" className="text-[color:var(--cx-fg-muted)]">
                      <Crop className="h-4 w-4 mr-1.5" />Recrop
                    </Button>
                  )}
                  {form.icon_image && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => set('icon_image', '')} data-testid="app-form-icon-image-remove" className="text-[color:var(--destructive)]">
                      <Trash2 className="h-4 w-4 mr-1.5" />Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[color:var(--cx-muted)]">{form.icon_image ? 'Using a custom image. Use Recrop to reframe, or Remove to use a preset icon.' : 'Upload a custom image (you can zoom & crop it), or pick a preset icon below.'}</p>
                <div className={`grid grid-cols-8 gap-1.5 ${form.icon_image ? 'opacity-40 pointer-events-none' : ''}`} data-testid="app-form-icon-picker">
                  {ICON_OPTIONS.map((name) => {
                    const Ic = getIcon(name);
                    const active = form.icon === name;
                    return (
                      <button type="button" key={name} onClick={() => set('icon', name)} title={name}
                        className={`h-9 w-9 rounded-lg flex items-center justify-center border transition-colors ${active ? 'border-[color:var(--cx-primary)] bg-[rgba(46,230,197,0.12)]' : 'border-[color:var(--cx-border)] hover:border-[rgba(46,230,197,0.4)]'}`}>
                        <Ic className="h-4 w-4" style={{ color: active ? 'var(--cx-primary)' : 'var(--cx-fg-muted)' }} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color picker */}
              <div className="space-y-2">
                <Label className="text-[color:var(--cx-fg-muted)]">Accent color</Label>
                <div className="flex flex-wrap gap-2" data-testid="app-form-color-picker">
                  {COLOR_OPTIONS.map((c) => {
                    const active = form.color === c.key;
                    return (
                      <button type="button" key={c.key} onClick={() => set('color', c.key)} title={c.key}
                        className={`h-8 w-8 rounded-full flex items-center justify-center border-2 transition-transform ${active ? 'scale-110' : 'hover:scale-105'}`}
                        style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})`, borderColor: active ? 'var(--cx-fg)' : 'transparent' }}>
                        {active && <Check className="h-4 w-4 text-[#04120f]" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3 pt-1">
                <div className="flex items-center justify-between rounded-xl border border-[color:var(--cx-border)] p-3">
                  <div>
                    <div className="text-sm text-[color:var(--cx-fg)]">Show on dashboard</div>
                    <div className="text-xs text-[color:var(--cx-muted)]">Disable to hide without deleting.</div>
                  </div>
                  <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="app-form-enabled" />
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-[color:var(--cx-border)] bg-[color:var(--cx-surface)] p-3 text-xs text-[color:var(--cx-muted)]">
                  <ExternalLink className="h-4 w-4 text-[color:var(--cx-primary)] shrink-0" />
                  <span>Tools launch in their own window, like opening a program.</span>
                </div>
              </div>

              {err && <p className="text-sm text-[color:var(--destructive)]" data-testid="app-form-error">{err}</p>}
            </div>
          </ScrollArea>

          <DialogFooter className="p-6 pt-4 border-t border-[color:var(--cx-border)] mt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="admin-save-app-button">
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>) : (isEdit ? 'Save changes' : 'Add app')}
            </Button>
          </DialogFooter>
        </form>

        <IconCropper
          open={cropperOpen}
          imageSrc={cropSrc}
          onCancel={() => { setCropperOpen(false); setCropSrc(''); }}
          onCropped={onCropped}
        />
      </DialogContent>
    </Dialog>
  );
}
